import React, { useEffect, useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

const ReportesRencar = () => {
  const [stats, setStats] = useState(null);
  const [rentasRecientes, setRentasRecientes] = useState([]);
  const [rentasCerradas, setRentasCerradas] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState("activas"); // activas | cerradas | todas
  const [cargando, setCargando] = useState(true);

  const cargarDatos = async () => {
    try {
      setCargando(true);
      
      // ✅ Cargar stats, rentas activas y cerradas
      const [resStats, resActivas, resCerradas] = await Promise.all([
        api.get("/rencar/dashboard/stats"),
        api.get("/rencar/rentas/abiertas"),
        api.get("/rencar/rentas/cerradas")
      ]);

      if (resStats.data) {
        setStats(resStats.data);
      }

      setRentasRecientes(resActivas.data.rentas || []);
      setRentasCerradas(resCerradas.data.rentas || []);
      
    } catch (error) {
      console.error("❌ Error cargando reportes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los reportes",
        confirmButtonColor: "#3b82f6"
      });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // 🔄 FILTRAR RENTAS SEGÚN TIPO
  // ═══════════════════════════════════════════════════════════════════
  const rentasFiltradas = () => {
    if (filtroTipo === "activas") return rentasRecientes;
    if (filtroTipo === "cerradas") return rentasCerradas;
    return [...rentasRecientes, ...rentasCerradas].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  // 📊 CALCULAR TOTALES
  // ═══════════════════════════════════════════════════════════════════
  const totalActivas = rentasRecientes.reduce((sum, r) => 
    sum + parseFloat(r.total_pagar || 0), 0
  );

  const totalCerradas = rentasCerradas.reduce((sum, r) => 
    sum + parseFloat(r.total_pagar || 0), 0
  );

  if (cargando) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Analizando historial financiero...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HEADER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Reportes Operativos</h1>
          <p style={styles.subtitle}>
            Resumen de ingresos y flujo de caja • {new Date().toLocaleDateString("es-DO")}
          </p>
        </div>
        <button style={styles.btnRefresh} onClick={cargarDatos}>
          🔄 Actualizar Datos
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* MÉTRICAS PRINCIPALES */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div style={styles.statsGrid}>
        <div style={{...styles.statCard, borderLeft: '4px solid #10b981'}}>
          <div style={{...styles.statIcon, background: '#d1fae5', color: '#10b981'}}>💰</div>
          <div>
            <div style={styles.statValue}>
              USD ${Number(stats?.ingresos_mes || 0).toLocaleString()}
            </div>
            <div style={styles.statLabel}>Ingresos del Mes</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: '4px solid #3b82f6'}}>
          <div style={{...styles.statIcon, background: '#dbeafe', color: '#3b82f6'}}>📈</div>
          <div>
            <div style={styles.statValue}>
              USD ${Number(stats?.ingresos_proyectados || 0).toLocaleString()}
            </div>
            <div style={styles.statLabel}>Ingresos Proyectados</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: '4px solid #f59e0b'}}>
          <div style={{...styles.statIcon, background: '#fef3c7', color: '#f59e0b'}}>🚗</div>
          <div>
            <div style={styles.statValue}>{stats?.rentas_activas || 0}</div>
            <div style={styles.statLabel}>Rentas Activas</div>
          </div>
        </div>

        <div style={{...styles.statCard, borderLeft: '4px solid #8b5cf6'}}>
          <div style={{...styles.statIcon, background: '#ede9fe', color: '#8b5cf6'}}>📊</div>
          <div>
            <div style={styles.statValue}>{stats?.porcentaje_ocupacion || 0}%</div>
            <div style={styles.statLabel}>Ocupación de Flota</div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ESTADÍSTICAS ADICIONALES */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div style={styles.statsRow}>
        <div style={styles.miniCard}>
          <span style={styles.miniIcon}>🏁</span>
          <div>
            <div style={styles.miniValue}>{rentasCerradas.length}</div>
            <div style={styles.miniLabel}>Rentas Completadas</div>
          </div>
        </div>

        <div style={styles.miniCard}>
          <span style={styles.miniIcon}>⚠️</span>
          <div>
            <div style={styles.miniValue}>{stats?.rentas_vencidas || 0}</div>
            <div style={styles.miniLabel}>Rentas Vencidas</div>
          </div>
        </div>

        <div style={styles.miniCard}>
          <span style={styles.miniIcon}>✅</span>
          <div>
            <div style={styles.miniValue}>{stats?.vehiculos_disponibles || 0}</div>
            <div style={styles.miniLabel}>Vehículos Disponibles</div>
          </div>
        </div>

        <div style={styles.miniCard}>
          <span style={styles.miniIcon}>🔧</span>
          <div>
            <div style={styles.miniValue}>{stats?.total_vehiculos || 0}</div>
            <div style={styles.miniLabel}>Total Flota</div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TABLA DE RENTAS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div style={{...styles.tableCard, marginTop: '32px'}}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>📋 Historial de Rentas</h3>
          
          {/* FILTROS */}
          <div style={styles.filterGroup}>
            <button
              style={filtroTipo === "activas" ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setFiltroTipo("activas")}
            >
              🟢 Activas ({rentasRecientes.length})
            </button>
            <button
              style={filtroTipo === "cerradas" ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setFiltroTipo("cerradas")}
            >
              🔒 Cerradas ({rentasCerradas.length})
            </button>
            <button
              style={filtroTipo === "todas" ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setFiltroTipo("todas")}
            >
              📊 Todas
            </button>
          </div>
        </div>

        <div style={styles.tableWrapper}>
          {rentasFiltradas().length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📭</div>
              <div style={styles.emptyText}>No hay rentas {filtroTipo}</div>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Fecha</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Vehículo</th>
                  <th style={styles.th}>Días</th>
                  <th style={styles.th}>Estado</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rentasFiltradas().slice(0, 20).map((renta) => (
                  <tr key={renta.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.idBadge}>#{renta.id}</span>
                    </td>
                    <td style={styles.td}>
                      {new Date(renta.fecha_salida).toLocaleDateString("es-DO")}
                    </td>
                    <td style={styles.td}>
                      <strong>{renta.Cliente?.nombre || "N/A"}</strong>
                    </td>
                    <td style={styles.td}>
                      {renta.Vehiculo?.marca} {renta.Vehiculo?.modelo}
                      <div style={styles.placa}>{renta.Vehiculo?.placa}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.diasBadge}>{renta.dias} días</span>
                    </td>
                    <td style={styles.td}>
                      {getEstadoBadge(renta.estado)}
                    </td>
                    <td style={{
                      ...styles.td, 
                      textAlign: 'right', 
                      color: '#10b981', 
                      fontWeight: 'bold',
                      fontSize: '15px'
                    }}>
                      USD ${parseFloat(renta.total_pagar || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* TOTALES */}
        {rentasFiltradas().length > 0 && (
          <div style={styles.tableFooter}>
            <div style={styles.totalRow}>
              <span>Total {filtroTipo}:</span>
              <span style={styles.totalAmount}>
                USD ${
                  (filtroTipo === "activas" ? totalActivas :
                   filtroTipo === "cerradas" ? totalCerradas :
                   totalActivas + totalCerradas).toLocaleString()
                }
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ANIMACIONES */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// 🎨 FUNCIÓN PARA BADGES DE ESTADO
// ═══════════════════════════════════════════════════════════════════
const getEstadoBadge = (estado) => {
  const badges = {
    abierta: { bg: '#fef3c7', color: '#92400e', text: '🟡 Abierta' },
    en_renta: { bg: '#dbeafe', color: '#1e40af', text: '🔵 En Renta' },
    en_uso: { bg: '#d1fae5', color: '#065f46', text: '🟢 En Uso' },
    cerrada: { bg: '#f3f4f6', color: '#374151', text: '⚫ Cerrada' }
  };

  const badge = badges[estado] || badges.abierta;

  return (
    <span style={{
      padding: '4px 12px',
      background: badge.bg,
      color: badge.color,
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    }}>
      {badge.text}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: "calc(100vh - 70px)",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    padding: "24px 32px",
    fontFamily: "'Segoe UI', sans-serif"
  },
  header: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    marginBottom: "32px" 
  },
  title: { 
    fontSize: "32px", 
    fontWeight: "700", 
    color: "#1e293b", 
    margin: 0 
  },
  subtitle: { 
    fontSize: "16px", 
    color: "#64748b", 
    marginTop: "4px" 
  },
  btnRefresh: { 
    padding: "12px 24px", 
    background: "white", 
    border: "2px solid #e2e8f0", 
    borderRadius: "12px", 
    cursor: "pointer", 
    fontWeight: "600",
    fontSize: "14px",
    transition: "all 0.2s"
  },
  
  // Stats Grid
  statsGrid: { 
    display: "grid", 
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", 
    gap: "20px",
    marginBottom: "24px"
  },
  statCard: { 
    background: "white", 
    padding: "24px", 
    borderRadius: "16px", 
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)", 
    border: "1px solid #e2e8f0", 
    display: "flex", 
    alignItems: "center", 
    gap: "20px" 
  },
  statIcon: { 
    width: "56px", 
    height: "56px", 
    borderRadius: "12px", 
    display: "flex", 
    alignItems: "center", 
    justifyContent: "center", 
    fontSize: "24px",
    flexShrink: 0
  },
  statValue: { 
    fontSize: "22px", 
    fontWeight: "700", 
    color: "#1e293b" 
  },
  statLabel: { 
    fontSize: "13px", 
    color: "#64748b", 
    marginTop: "4px" 
  },

  // Mini Cards Row
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px"
  },
  miniCard: {
    background: "white",
    padding: "16px 20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  miniIcon: {
    fontSize: "24px",
    flexShrink: 0
  },
  miniValue: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b"
  },
  miniLabel: {
    fontSize: "12px",
    color: "#64748b"
  },

  // Tabla
  tableCard: { 
    background: "white", 
    borderRadius: "16px", 
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)", 
    border: "1px solid #e2e8f0", 
    overflow: "hidden" 
  },
  tableHeader: { 
    padding: "20px 24px", 
    borderBottom: "1px solid #e2e8f0", 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px"
  },
  tableTitle: { 
    fontSize: "18px", 
    fontWeight: "600", 
    color: "#1e293b", 
    margin: 0 
  },
  
  // Filtros
  filterGroup: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  filterBtn: {
    padding: "8px 16px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    color: "#64748b",
    transition: "all 0.2s"
  },
  filterBtnActive: {
    padding: "8px 16px",
    background: "#3b82f6",
    border: "1px solid #3b82f6",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    color: "white",
    transition: "all 0.2s"
  },

  tableWrapper: {
    overflowX: "auto"
  },
  table: { 
    width: "100%", 
    borderCollapse: "collapse" 
  },
  th: { 
    padding: "12px 16px", 
    textAlign: "left", 
    fontSize: "12px", 
    color: "#64748b", 
    textTransform: "uppercase", 
    background: "#f8fafc", 
    borderBottom: "2px solid #e2e8f0",
    fontWeight: "600"
  },
  tr: { 
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.2s"
  },
  td: { 
    padding: "14px 16px", 
    fontSize: "14px", 
    color: "#334155" 
  },

  // Badges
  idBadge: {
    padding: "4px 8px",
    background: "#f1f5f9",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569"
  },
  placa: {
    fontSize: "11px",
    color: "#94a3b8",
    marginTop: "2px"
  },
  diasBadge: {
    padding: "4px 8px",
    background: "#ede9fe",
    color: "#6b21a8",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600"
  },

  // Footer
  tableFooter: {
    padding: "16px 24px",
    borderTop: "2px solid #e2e8f0",
    background: "#f8fafc"
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b"
  },
  totalAmount: {
    fontSize: "20px",
    color: "#10b981"
  },

  // Empty State
  emptyState: {
    padding: "60px 20px",
    textAlign: "center"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "16px"
  },
  emptyText: {
    fontSize: "16px",
    color: "#64748b",
    fontWeight: "600"
  },

  // Loading
  loadingContainer: { 
    minHeight: "60vh", 
    display: "flex", 
    flexDirection: "column", 
    alignItems: "center", 
    justifyContent: "center" 
  },
  spinner: { 
    width: "40px", 
    height: "40px", 
    border: "4px solid #e2e8f0", 
    borderTopColor: "#3b82f6", 
    borderRadius: "50%", 
    animation: "spin 0.8s linear infinite" 
  },
  loadingText: {
    marginTop: "16px",
    fontSize: "16px",
    color: "#64748b",
    fontWeight: "600"
  }
};

export default ReportesRencar;